import { useCallback, useRef, useState } from 'react';
import type { AquariumRuntimeView, RuntimeMenuPanel } from '../worldRuntime/types.js';
import { fetchHistoryPanelStory } from '../worldRuntime/dayStory.js';

export function useHistoryPanelOpen(args: {
  view: AquariumRuntimeView;
  setPanel: (panel: RuntimeMenuPanel) => void;
  setHistoryCharId: (id: string | null) => void;
  closeMenu: () => void;
}): {
  dayStory: string | null;
  openLocationHistory: () => Promise<void>;
  openCharacterHistoryPick: () => Promise<void>;
  openCharacterHistory: (characterId: string) => void;
  cancelPending: () => void;
  clearDayStories: () => void;
} {
  const { view, setPanel, setHistoryCharId, closeMenu } = args;
  const [dayStory, setDayStory] = useState<string | null>(null);
  const openGen = useRef(0);

  const buildStoryArgs = useCallback(() => ({
    events: view.historyEvents,
    schedule: view.snapshot.schedule,
    worldClockIso: view.clock.inWorldIso,
    locationRegion: view.snapshot.location.region,
    roster: view.handoff.roster.map((npc) => ({ id: npc.id, firstName: npc.firstName })),
  }), [view]);

  const fetchDayStory = useCallback(async (): Promise<string | null> => {
    return fetchHistoryPanelStory(buildStoryArgs());
  }, [buildStoryArgs]);

  const openLocationHistory = useCallback(async (): Promise<void> => {
    closeMenu();
    const gen = ++openGen.current;
    const story = await fetchDayStory();
    if (gen !== openGen.current) return;
    setDayStory(story);
    setPanel('location-history');
  }, [closeMenu, fetchDayStory, setPanel]);

  const openCharacterHistoryPick = useCallback(async (): Promise<void> => {
    closeMenu();
    const gen = ++openGen.current;
    const story = await fetchDayStory();
    if (gen !== openGen.current) return;
    setDayStory(story);
    setHistoryCharId(null);
    setPanel('character-history-pick');
  }, [closeMenu, fetchDayStory, setHistoryCharId, setPanel]);

  const openCharacterHistory = useCallback((characterId: string): void => {
    setHistoryCharId(characterId);
    setPanel('character-history');
  }, [setHistoryCharId, setPanel]);

  const cancelPending = useCallback((): void => {
    openGen.current += 1;
  }, []);

  const clearDayStories = useCallback((): void => {
    cancelPending();
    setDayStory(null);
  }, [cancelPending]);

  return {
    dayStory,
    openLocationHistory,
    openCharacterHistoryPick,
    openCharacterHistory,
    cancelPending,
    clearDayStories,
  };
}
